/*
 * mowd-measure-lawn.js — Mowd "Measure My Lawn" tool (clean rebuild)
 * Replaces the obfuscated assets/measure-lawn.js.
 *
 * Loaded with `defer`. Exposes window.initMowdMeasure as the Google Maps
 * callback (defined synchronously at top level so it exists whenever Maps
 * resolves). Config comes from data-* attributes on #mowd-measure.
 *
 * Drawing: Google removed google.maps.drawing.DrawingManager in Maps JS API
 * v3.65, so the lawn outline is built from map click events and a standard
 * (editable) google.maps.Polygon, with the geometry library for area.
 * A crosshair cursor, a white dot at each placed corner, and a dashed
 * "rubber-band" line from the last corner to the cursor make plotting precise.
 *
 * Exclusions: after the lawn is drawn, the user can outline areas to omit
 * (flower beds, patio, pond…). Each exclusion is a red editable polygon that
 * MUST sit inside the lawn (validated with geometry.poly.containsLocation).
 * The net total = lawn area − sum of omitted areas, updated live.
 */
(function () {
  'use strict';

  var LAWN = { fill: '#34903b', stroke: '#203d4f' };
  var OMIT = { fill: '#d23b3b', stroke: '#7a1414' };

  var map = null;
  var lawnPolygon = null;        // the single green lawn polygon (gross area)
  var exclusions = [];           // array of red exclusion polygons
  var currentExclusion = null;   // the exclusion currently being drawn
  var guideLine = null;          // dashed rubber-band line to the cursor
  var mode = 'lawn';             // 'lawn' | 'exclude'
  var geocoder = null;
  var cfg = {};

  function $(id) { return document.getElementById(id); }
  function geo() { return window.google && google.maps && google.maps.geometry; }

  function readConfig() {
    var root = $('mowd-measure');
    if (!root) return null;
    return {
      planUrl: root.getAttribute('data-plan-url') || '/pages/lawn-plan-selector',
      lat: parseFloat(root.getAttribute('data-lat')) || 52.1917,
      lng: parseFloat(root.getAttribute('data-lng')) || -1.7083,
      zoom: parseInt(root.getAttribute('data-zoom'), 10) || 6,
      foundZoom: parseInt(root.getAttribute('data-found-zoom'), 10) || 19
    };
  }

  function setStatus(msg, isError) {
    var el = $('mowd-status');
    if (!el) return;
    el.textContent = msg || '';
    el.classList.toggle('mowd-status--error', !!isError);
    el.style.display = msg ? 'block' : 'none';
  }

  function fmt(n) { return Math.round(n).toLocaleString('en-GB'); }

  // A white dot marker at a placed corner (so even the first point is visible).
  function addDot(poly, latLng, stroke) {
    if (!poly._dots) poly._dots = [];
    poly._dots.push(new google.maps.Marker({
      position: latLng, map: map, clickable: false, zIndex: 5,
      icon: { path: google.maps.SymbolPath.CIRCLE, scale: 5,
        fillColor: '#ffffff', fillOpacity: 1, strokeColor: stroke, strokeWeight: 2 }
    }));
  }
  function removeDots(poly) {
    if (poly && poly._dots) {
      for (var k = 0; k < poly._dots.length; k++) poly._dots[k].setMap(null);
      poly._dots = [];
    }
  }

  // Rubber-band guide line from the last placed corner to the cursor.
  function updateGuide(cursor) {
    var shape = (mode === 'exclude') ? currentExclusion : lawnPolygon;
    if (!shape || shape.getPath().getLength() < 1 || !cursor) {
      if (guideLine) guideLine.setMap(null);
      return;
    }
    var path = shape.getPath();
    var last = path.getAt(path.getLength() - 1);
    if (!guideLine) {
      guideLine = new google.maps.Polyline({
        clickable: false, zIndex: 4, strokeOpacity: 0,
        icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: '#ffffff', scale: 3 }, offset: '0', repeat: '9px' }]
      });
    }
    guideLine.setPath([last, cursor]);
    guideLine.setMap(map);
  }

  // Reflect button availability for the exclusion controls.
  function syncControls() {
    var lawnReady = !!(lawnPolygon && lawnPolygon.getPath().getLength() >= 3);
    var addBtn = $('mowd-add-exclusion');
    var undoBtn = $('mowd-remove-exclusion');
    if (addBtn) {
      addBtn.disabled = !lawnReady;
      addBtn.classList.toggle('is-active', mode === 'exclude');
      addBtn.textContent = mode === 'exclude' ? 'Add another excluded area' : 'Exclude an area';
    }
    if (undoBtn) {
      undoBtn.style.display = exclusions.length ? 'inline-flex' : 'none';
    }
  }

  function areaOf(poly) {
    if (!poly || !geo()) return 0;
    return google.maps.geometry.spherical.computeArea(poly.getPath());
  }

  // Compute gross / omitted / net and update the result + breakdown + CTA.
  function refresh() {
    var gross = areaOf(lawnPolygon);
    var omitted = 0;
    for (var i = 0; i < exclusions.length; i++) omitted += areaOf(exclusions[i]);
    if (omitted > gross) omitted = gross;
    var net = Math.max(0, gross - omitted);

    var m2 = $('mowd-area-m2');
    var ft2 = $('mowd-area-ft2');
    var goBtn = $('mowd-goto-plan');
    var rounded = Math.round(net);
    if (m2) m2.textContent = fmt(net);
    if (ft2) ft2.textContent = fmt(net * 10.7639);
    if (goBtn) {
      if (rounded > 0) {
        goBtn.removeAttribute('disabled');
        goBtn.setAttribute('data-area', String(rounded));
      } else {
        goBtn.setAttribute('disabled', 'disabled');
        goBtn.removeAttribute('data-area');
      }
    }

    var bd = $('mowd-breakdown');
    if (bd) {
      if (omitted > 0 && gross > 0) {
        bd.innerHTML = 'Lawn ' + fmt(gross) + ' m² − excluded ' + fmt(omitted) +
          ' m² = <strong>' + fmt(net) + ' m²</strong>';
        bd.style.display = 'block';
      } else {
        bd.style.display = 'none';
        bd.innerHTML = '';
      }
    }
    syncControls();
  }

  function watchPath(poly) {
    var path = poly.getPath();
    google.maps.event.addListener(path, 'set_at', refresh);
    google.maps.event.addListener(path, 'insert_at', refresh);
    google.maps.event.addListener(path, 'remove_at', refresh);
  }

  // --- Lawn drawing ---
  function addLawnPoint(latLng) {
    if (!lawnPolygon) {
      lawnPolygon = new google.maps.Polygon({
        paths: [latLng], fillColor: LAWN.fill, fillOpacity: 0.35,
        strokeColor: LAWN.stroke, strokeWeight: 3, clickable: false,
        editable: true, zIndex: 1, map: map
      });
      watchPath(lawnPolygon);
    } else {
      lawnPolygon.getPath().push(latLng);
    }
    addDot(lawnPolygon, latLng, LAWN.stroke);
    refresh();
    var n = lawnPolygon.getPath().getLength();
    if (n >= 3) {
      setStatus('Looking good — drag to fine-tune, exclude any non-lawn areas, then get your plan.', false);
    } else {
      setStatus('Keep tapping the corners of your lawn (you need at least three).', false);
    }
  }

  // --- Exclusion drawing ---
  function addExclusionPoint(latLng) {
    if (!lawnPolygon || lawnPolygon.getPath().getLength() < 3) {
      setStatus('Draw your lawn first, then exclude areas inside it.', true);
      return;
    }
    if (!google.maps.geometry.poly.containsLocation(latLng, lawnPolygon)) {
      setStatus('Excluded areas must be inside your lawn — tap inside the green outline.', true);
      return;
    }
    if (!currentExclusion) {
      currentExclusion = new google.maps.Polygon({
        paths: [latLng], fillColor: OMIT.fill, fillOpacity: 0.4,
        strokeColor: OMIT.stroke, strokeWeight: 2, clickable: false,
        editable: true, zIndex: 2, map: map
      });
      exclusions.push(currentExclusion);
      watchPath(currentExclusion);
    } else {
      currentExclusion.getPath().push(latLng);
    }
    addDot(currentExclusion, latLng, OMIT.stroke);
    refresh();
    var n = currentExclusion.getPath().getLength();
    if (n >= 3) {
      setStatus('Excluding this area. Add more corners, start another with “Exclude an area”, or get your plan.', false);
    } else {
      setStatus('Tap the corners of the area to exclude (you need at least three).', false);
    }
  }

  function startExclusion() {
    if (!lawnPolygon || lawnPolygon.getPath().getLength() < 3) {
      setStatus('Draw your lawn first, then exclude areas inside it.', true);
      return;
    }
    mode = 'exclude';
    currentExclusion = null; // next tap starts a fresh omitted area
    if (guideLine) guideLine.setMap(null);
    setStatus('Tap inside your lawn to outline an area to exclude (a flower bed, patio, shed…).', false);
    syncControls();
  }

  function removeLastExclusion() {
    var ex = exclusions.pop();
    if (ex) { removeDots(ex); ex.setMap(null); }
    currentExclusion = null;
    if (!exclusions.length) mode = 'lawn';
    refresh();
    setStatus(exclusions.length ? 'Removed the last excluded area.' : 'All excluded areas cleared — your full lawn area is shown.', false);
  }

  function clearAll() {
    if (lawnPolygon) { removeDots(lawnPolygon); lawnPolygon.setMap(null); lawnPolygon = null; }
    for (var i = 0; i < exclusions.length; i++) { removeDots(exclusions[i]); exclusions[i].setMap(null); }
    exclusions = [];
    currentExclusion = null;
    mode = 'lawn';
    if (guideLine) guideLine.setMap(null);
    refresh();
    setStatus('Cleared. Tap each corner of your lawn to measure again.', false);
  }

  // --- Map / address helpers ---
  function lookupPostcode() {
    var input = $('mowd-postcode');
    if (!input || !input.value.trim()) { setStatus('Please enter your postcode.', true); return; }
    if (!geocoder) geocoder = new google.maps.Geocoder();
    setStatus('Finding your address…', false);
    geocoder.geocode(
      { address: input.value.trim(), componentRestrictions: { country: 'GB' } },
      function (results, status) {
        if (status === 'OK' && results[0] && results[0].geometry) {
          map.setCenter(results[0].geometry.location);
          map.setZoom(cfg.foundZoom);
          setStatus('Now tap each corner of your lawn on the map.', false);
        } else {
          setStatus('We couldn’t find that postcode. Check it and try again.', true);
        }
      }
    );
  }

  function useMyLocation() {
    if (!navigator.geolocation) {
      setStatus('Your browser doesn’t support location. Enter a postcode instead.', true);
      return;
    }
    setStatus('Locating you…', false);
    navigator.geolocation.getCurrentPosition(
      function (pos) {
        map.setCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        map.setZoom(cfg.foundZoom);
        setStatus('Now tap each corner of your lawn on the map.', false);
      },
      function () { setStatus('We couldn’t get your location. Enter a postcode instead.', true); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }

  function goToPlan() {
    var btn = $('mowd-goto-plan');
    var area = btn && btn.getAttribute('data-area');
    if (!area) return;
    window.location.href = cfg.planUrl +
      (cfg.planUrl.indexOf('?') > -1 ? '&' : '?') + 'lawnArea=' + encodeURIComponent(area);
  }

  function wireControls() {
    var form = $('mowd-postcode-form');
    if (form) form.addEventListener('submit', function (e) { e.preventDefault(); lookupPostcode(); });
    var lookupBtn = $('mowd-lookup');
    if (lookupBtn) lookupBtn.addEventListener('click', function (e) { e.preventDefault(); lookupPostcode(); });
    var clearBtn = $('mowd-clear');
    if (clearBtn) clearBtn.addEventListener('click', function (e) { e.preventDefault(); clearAll(); });
    var locateBtn = $('mowd-locate');
    if (locateBtn) locateBtn.addEventListener('click', function (e) { e.preventDefault(); useMyLocation(); });
    var addEx = $('mowd-add-exclusion');
    if (addEx) addEx.addEventListener('click', function (e) { e.preventDefault(); startExclusion(); });
    var rmEx = $('mowd-remove-exclusion');
    if (rmEx) rmEx.addEventListener('click', function (e) { e.preventDefault(); removeLastExclusion(); });
    var goBtn = $('mowd-goto-plan');
    if (goBtn) goBtn.addEventListener('click', function (e) { e.preventDefault(); goToPlan(); });
  }

  // Google Maps callback — defined synchronously so it exists whenever Maps resolves.
  window.initMowdMeasure = function initMowdMeasure() {
    cfg = readConfig() || {};
    var mapEl = $('mowd-map');
    if (!mapEl || !geo() || map) return;

    map = new google.maps.Map(mapEl, {
      center: { lat: cfg.lat, lng: cfg.lng },
      zoom: cfg.zoom, mapTypeId: 'hybrid', mapTypeControl: true,
      streetViewControl: false, fullscreenControl: true, gestureHandling: 'greedy',
      draggableCursor: 'crosshair'
    });

    var loading = $('mowd-map-loading');
    if (loading) loading.style.display = 'none';

    map.addListener('click', function (e) {
      if (mode === 'exclude') addExclusionPoint(e.latLng);
      else addLawnPoint(e.latLng);
    });
    map.addListener('mousemove', function (e) { updateGuide(e.latLng); });

    setStatus('Enter your postcode (or use your location), then tap each corner of your lawn.', false);
    syncControls();
  };

  function start() {
    wireControls();
    refresh();
    if (geo() && !map) window.initMowdMeasure();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
